import httpCommon from "./http-common";
import { AxiosProgressEvent } from "axios";

// Get all recipes
const getAllRecipes = (businessId: number, skip = 0, limit = 0) => {
  return httpCommon.get(`/v1/get/recipes/${businessId}?skip=${skip}&limit=${limit}`);
};

// Get recipe by ID
const getRecipe = (recipeId: string) => {
  return httpCommon.get(`/v1/get/recipe/${recipeId}`);
};

// Create recipe
const createRecipe = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/recipe/${businessId}`, data);
};

// Update recipe
const updateRecipe = (recipeId: string, data: any) => {
  return httpCommon.put(`/v1/update/recipe/${recipeId}`, data);
};

// Delete recipe
const deleteRecipe = (recipeId: string) => {
  return httpCommon.delete(`/v1/delete/recipe/${recipeId}`);
};

// Search recipes
const searchRecipes = (businessId: number, searchTerm: string) => {
  return httpCommon.get(`/v1/search/recipes/${businessId}/${searchTerm}`);
};

// Get recipe items (Bill of Materials)
const getRecipeItems = (recipeId: string) => {
  return httpCommon.get(`/v1/get/recipe/items/${recipeId}`);
};

// Add recipe item
const addRecipeItem = (recipeId: string, data: any) => {
  return httpCommon.post(`/v1/add/recipe/item/${recipeId}`, data);
};

// Update recipe item
const updateRecipeItem = (recipeItemId: string, data: any) => {
  return httpCommon.put(`/v1/update/recipe/item/${recipeItemId}`, data);
};

// Delete recipe item
const deleteRecipeItem = (recipeItemId: string) => {
  return httpCommon.delete(`/v1/delete/recipe/item/${recipeItemId}`);
};

// Calculate recipe cost
const calculateRecipeCost = (recipeId: string, quantity: number = 1) => {
  return httpCommon.get(`/v1/calculate/recipe/cost/${recipeId}?quantity=${quantity}`);
};

// Check recipe availability
const checkRecipeAvailability = (recipeId: string, quantity: number, warehouseId?: string) => {
  const warehouseParam = warehouseId ? `&warehouseId=${warehouseId}` : '';
  return httpCommon.get(`/v1/check/recipe/availability/${recipeId}?quantity=${quantity}${warehouseParam}`);
};

// Production Jobs

// Get all production jobs
const getAllProductionJobs = (businessId: number, status?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (status) queryParams += `&status=${status}`;
  return httpCommon.get(`/v1/get/production/jobs/${businessId}${queryParams}`);
};

// Get production job by ID
const getProductionJob = (jobId: string) => {
  return httpCommon.get(`/v1/get/production/job/${jobId}`);
};

// Create production job
const createProductionJob = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/production/job/${businessId}`, data);
};

// Update production job
const updateProductionJob = (jobId: string, data: any) => {
  return httpCommon.put(`/v1/update/production/job/${jobId}`, data);
};

// Delete production job
const deleteProductionJob = (jobId: string) => {
  return httpCommon.delete(`/v1/delete/production/job/${jobId}`);
};

// Start production job
const startProductionJob = (jobId: string, data: any) => {
  return httpCommon.post(`/v1/start/production/job/${jobId}`, data);
};

// Complete production job
const completeProductionJob = (jobId: string, data: any) => {
  return httpCommon.post(`/v1/complete/production/job/${jobId}`, data);
};

// Pause production job
const pauseProductionJob = (jobId: string, reason?: string) => {
  return httpCommon.post(`/v1/pause/production/job/${jobId}`, { reason });
};

// Resume production job
const resumeProductionJob = (jobId: string) => {
  return httpCommon.post(`/v1/resume/production/job/${jobId}`);
};

// Cancel production job
const cancelProductionJob = (jobId: string, reason: string) => {
  return httpCommon.post(`/v1/cancel/production/job/${jobId}`, { reason });
};

// Get production job items (materials and outputs)
const getProductionJobItems = (jobId: string) => {
  return httpCommon.get(`/v1/get/production/job/items/${jobId}`);
};

// Update production job item status
const updateProductionJobItemStatus = (jobItemId: string, data: any) => {
  return httpCommon.put(`/v1/update/production/job/item/${jobItemId}`, data);
};

// Record material consumption
const recordMaterialConsumption = (jobId: string, data: any) => {
  return httpCommon.post(`/v1/record/material/consumption/${jobId}`, data);
};

// Record production output
const recordProductionOutput = (jobId: string, data: any) => {
  return httpCommon.post(`/v1/record/production/output/${jobId}`, data);
};

// Get production schedule
const getProductionSchedule = (businessId: number, startDate?: string, endDate?: string, warehouseId?: string) => {
  let queryParams = '';
  const params = [];
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (warehouseId) params.push(`warehouseId=${warehouseId}`);
  if (params.length > 0) queryParams = '?' + params.join('&');
  
  return httpCommon.get(`/v1/get/production/schedule/${businessId}${queryParams}`);
};

// Create production schedule
const createProductionSchedule = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/production/schedule/${businessId}`, data);
};

// Update production schedule
const updateProductionSchedule = (scheduleId: string, data: any) => {
  return httpCommon.put(`/v1/update/production/schedule/${scheduleId}`, data);
};

// Get production capacity
const getProductionCapacity = (businessId: number, warehouseId?: string, date?: string) => {
  let queryParams = '';
  const params = [];
  if (warehouseId) params.push(`warehouseId=${warehouseId}`);
  if (date) params.push(`date=${date}`);
  if (params.length > 0) queryParams = '?' + params.join('&');
  
  return httpCommon.get(`/v1/get/production/capacity/${businessId}${queryParams}`);
};

// Get production efficiency metrics
const getProductionEfficiency = (businessId: number, startDate: string, endDate: string, warehouseId?: string) => {
  let queryParams = `?startDate=${startDate}&endDate=${endDate}`;
  if (warehouseId) queryParams += `&warehouseId=${warehouseId}`;
  
  return httpCommon.get(`/v1/get/production/efficiency/${businessId}${queryParams}`);
};

// Get work orders
const getWorkOrders = (businessId: number, status?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (status) queryParams += `&status=${status}`;
  return httpCommon.get(`/v1/get/work/orders/${businessId}${queryParams}`);
};

// Create work order
const createWorkOrder = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/work/order/${businessId}`, data);
};

// Update work order status
const updateWorkOrderStatus = (workOrderId: string, status: string, data?: any) => {
  return httpCommon.put(`/v1/update/work/order/status/${workOrderId}`, { status, ...data });
};

// Get quality control checks
const getQualityControlChecks = (businessId: number, jobId?: string) => {
  const jobParam = jobId ? `?jobId=${jobId}` : '';
  return httpCommon.get(`/v1/get/quality/control/checks/${businessId}${jobParam}`);
};

// Create quality control check
const createQualityControlCheck = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/quality/control/check/${businessId}`, data);
};

// Update quality control check results
const updateQualityControlResults = (checkId: string, data: any) => {
  return httpCommon.put(`/v1/update/quality/control/results/${checkId}`, data);
};

// Generate production report
const generateProductionReport = (businessId: number, reportType: string, startDate: string, endDate: string) => {
  return httpCommon.get(`/v1/generate/production/report/${businessId}/${reportType}?startDate=${startDate}&endDate=${endDate}`, {
    responseType: 'blob'
  });
};

const ManufacturingService = {
  // Recipes (Bill of Materials)
  getAllRecipes,
  getRecipe,
  createRecipe,
  updateRecipe,
  deleteRecipe,
  searchRecipes,
  
  // Recipe Items
  getRecipeItems,
  addRecipeItem,
  updateRecipeItem,
  deleteRecipeItem,
  
  // Recipe Analysis
  calculateRecipeCost,
  checkRecipeAvailability,
  
  // Production Jobs
  getAllProductionJobs,
  getProductionJob,
  createProductionJob,
  updateProductionJob,
  deleteProductionJob,
  
  // Production Job Control
  startProductionJob,
  completeProductionJob,
  pauseProductionJob,
  resumeProductionJob,
  cancelProductionJob,
  
  // Production Job Items
  getProductionJobItems,
  updateProductionJobItemStatus,
  recordMaterialConsumption,
  recordProductionOutput,
  
  // Production Scheduling
  getProductionSchedule,
  createProductionSchedule,
  updateProductionSchedule,
  
  // Production Analytics
  getProductionCapacity,
  getProductionEfficiency,
  
  // Work Orders
  getWorkOrders,
  createWorkOrder,
  updateWorkOrderStatus,
  
  // Quality Control
  getQualityControlChecks,
  createQualityControlCheck,
  updateQualityControlResults,
  
  // Reporting
  generateProductionReport
};

export default ManufacturingService; 